import { NoyauTextsFields } from '@/components/admin/reglages/noyau-texts-fields';
import { VersionedEditor } from '@/components/admin/versioned/versioned-editor';
import { useAdminSession } from '@/lib/admin-session';
import { noyauTextsApi } from '@/lib/api';
import { noyauTextsBrowserDraft } from '@/lib/noyau-texts-browser-draft';
import type { NoyauTextsDraft } from '@/lib/noyau-texts-draft';
import { NOYAU_TEXTS_DRAFT_KIT } from '@/lib/noyau-texts-draft';
import { NOYAU_TEXTS_WORDING } from '@/lib/noyau-texts-wording';
import type {
  NoyauTextsContent,
  NoyauTextsDetail,
  NoyauTextsDraftContent,
  NoyauTextsUsage,
  NoyauTextsVersion,
  VersionSummary,
} from '@matter_extractor/shared';
import { useEffect } from 'react';

/**
 * The editor of « Les textes des noyaux » (noyau.md §6, issue #148): the
 * pattern of §4 in its `settings` logement, plus the eighteen texts.
 *
 * Everything the four editors before it do identically - the head, the
 * history, « utilisé dans », the save box and « Passer en production », the
 * debounced autosave and its optimistic control, undo and redo - is
 * `VersionedEditor`. What is here is what only these texts know: their API,
 * their draft kit, their wording, their form, the logement of a singleton with
 * no library around it - and, since the review of !462, the two openings of the
 * layer no other object passes.
 *
 * ## Before the v1, the entry is kept in this browser
 *
 * There is no entity yet, so no server draft (N8), and seven texts typed on
 * Monday must still be there on Thursday (N7). `browserDraft` is handed to the
 * layer only while `detail === null`, and the layer reads it once, writes it at
 * the autosave's rhythm and clears it on the first save that lands
 * (`lib/noyau-texts-browser-draft.ts`). Once a version exists - written here or
 * from another browser - what this browser kept can never be read again (a
 * singleton has no way back to « aucune version »), so it is forgotten the
 * first time the tab opens on one.
 *
 * ## The boxes read who plays each pointer of the TEXTS
 *
 * `usage` is `GET /api/noyau-texts/{id}/usage`: the experiences split by the
 * tag of their reference (§6.5), served before the first version as well. The
 * layer draws it in the save box and in « Passer en production » instead of its
 * own filter, which reads the referents' pointers and not what they read.
 * « Utilisé dans », in the drawer, keeps the layer's rule and the detail.
 *
 * ## The cache keys
 *
 * `['noyau-texts', email, NOYAU_TEXTS_ID]` holds the detail, and the layer
 * writes every answer into it. There is no list to invalidate - a singleton
 * has none - so the second prefix names one nobody reads, rather than the
 * detail's own, which a prefix invalidation would refetch after every
 * autosave. `['noyau-texts-usage', email]` holds the split usage: no write of
 * the texts moves it, only the experiences do.
 *
 * ## No navigation after the first save
 *
 * The criterion's first save navigates to the new entity's address; the texts
 * have only the one, and it is already on screen. The layer writes the new
 * detail into the cache, the route reads it back, and its key moves from the
 * empty editor to the entity's - which is the remount the criterion gets from
 * its navigation.
 */

/** The prefix of the detail's query key, shared with the route that reads it. */
export const NOYAU_TEXTS_QUERY_KEY = 'noyau-texts';

/** The prefix of the split usage's query key, read by the route as well. */
export const NOYAU_TEXTS_USAGE_QUERY_KEY = 'noyau-texts-usage';

export interface NoyauTextsEditorProps {
  /** The singleton, or null while no version exists (§6.4). */
  detail: NoyauTextsDetail | null;
  /** Who reads which pointer of the texts, by the tag of each reference (§6.5). */
  usage: NoyauTextsUsage;
  /** A version opened read-only by « Voir », or null. */
  viewing: NoyauTextsVersion | null;
  onCloseView: () => void;
  onView: (version: VersionSummary) => void;
  /** Re-read the texts from the server - what the stale-draft box offers. */
  onReload: () => void;
}

export function NoyauTextsEditor({
  detail,
  usage,
  viewing,
  onCloseView,
  onView,
  onReload,
}: NoyauTextsEditorProps) {
  const { email } = useAdminSession();
  const born = detail !== null;

  /*
    An EFFECT in a repo that proscribes them, and the ban holds: it fetches
    nothing, it removes one key this browser wrote before the v1 - see the head
    of this file for why it can never be read again.
  */
  useEffect(() => {
    if (born) noyauTextsBrowserDraft(email).clear();
  }, [born, email]);

  return (
    <VersionedEditor<NoyauTextsContent, NoyauTextsDraftContent, NoyauTextsDraft>
      wording={NOYAU_TEXTS_WORDING}
      api={noyauTextsApi}
      kit={NOYAU_TEXTS_DRAFT_KIT}
      keys={{ detail: NOYAU_TEXTS_QUERY_KEY, list: 'noyau-texts-sans-liste' }}
      hasReferents
      detail={detail}
      viewing={viewing}
      onCloseView={onCloseView}
      onView={onView}
      onReload={onReload}
      onCreated={() => undefined}
      layout="settings"
      referentsByTag={usage}
      {...(born ? {} : { browserDraft: noyauTextsBrowserDraft(email) })}
      renderFields={({ draft, readOnly, onDraft }) => (
        <NoyauTextsFields draft={draft} readOnly={readOnly} empty={!born} onDraft={onDraft} />
      )}
    />
  );
}
